import Logger from "../../clients/sentry";
import { handleError } from "../../errors";
import { ChartOption, ChartOptionItem } from "../../excel/charts/api";
import { LoggingInfo } from "./types";
import { activateChart as activateChartInWorkbook } from "../../excel/charts/api/operations";

export function useChartActions() {
  const withChartLogging = async (callback: () => Promise<any>, loggingInfo?: LoggingInfo) => {
    try {
      await callback();
    } catch (error) {
      const err = handleError(error, {
        context: loggingInfo?.context || "withChartLogging",
        chartType: loggingInfo?.chartType,
        chartName: loggingInfo?.chartName,
      });
      const message = loggingInfo?.message || "Error generating chart";
      Logger.error(message, err);
      throw err;
    }
  };

  const updateChartOption = async (
    worksheetName: string,
    chartName: string,
    option: ChartOption,
    optionItem: ChartOptionItem,
    chartOptions: Record<string, ChartOption>
  ): Promise<Record<string, string>> => {
    if (!optionItem) return {};

    const stateUpdates: Record<string, string> = {};

    try {
      // Handle dependent options
      if (option.key === "breakdown") {
        // When breakdown changes, also update includeBreakdowns
        const includeBreakdownsOption = chartOptions["includeBreakdowns"];
        if (includeBreakdownsOption) {
          const newValue = { key: "true", label: "Yes" };
          stateUpdates.includeBreakdowns = newValue.key;
          if (includeBreakdownsOption.onChange) {
            await includeBreakdownsOption.onChange(worksheetName, chartName, newValue);
          }
        }
      }

      // Call the original onChange handler
      if (option.onChange) {
        await option.onChange(worksheetName, chartName, optionItem);
      }

      // Update local state after Excel changes are complete
      stateUpdates[option.key] = optionItem.key;
      return stateUpdates;
    } catch (error) {
      const err = handleError(error, { optionLabel: option.label, context: "updateChartOption" });
      Logger.error("Failed to update chart option", err);
      throw err;
    }
  };

  const activateChart = async (worksheetName: string, chartName: string) => {
    try {
      await activateChartInWorkbook(worksheetName, chartName);
    } catch (error) {
      const err = handleError(error, { worksheetName, chartName, context: "activateChart" });
      Logger.error("Error activating chart", err);
      throw err;
    }
  };

  return {
    withChartLogging,
    updateChartOption,
    activateChart,
  };
}
